import { FC, useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import type { PaginationProps } from 'antd';
import { keepPreviousData } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import {
  ISODate,
  isSafeFiniteInteger,
  useAlertContext,
  Spinner,
  useLoadViewState,
  useQueryParams,
  useDirectory,
  useSaveDirectoryState,
  SampleKey,
  useSampleEditSessionQuery,
  useUpdateSampleDraftsMutation,
  useInsertSampleDraftMutation,
  useDeleteSampleDraftsMutation,
  useCommitSampleDraftsMutation,
  useDiscardSampleEditSessionMutation,
  useValidateSampleRowMutation,
  useSamplesQuery,
  EditSessionContextProvider,
  useClampPageToBounds,
  saveViewState,
  serializeSort as defaultSerializeSort,
} from '@atlas-frontend/sdk';
import { useNumericParam } from '../../common/useEntityIdParam';
import { NotFound } from '../NotFound';
import { SampleDetailForm } from './SampleDetailForm';
import { SampleDataObjectType } from './SampleSearch';
import { SampleContextProvider, useSampleContext } from './state/SampleContext';
import type { ViewType } from './state/SampleContext.types';
import { SampleDetailsContextProvider } from './state/SampleDetailsContext';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SampleDetail.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'sample-detail-directory',
);
addTranslations(translations);

export const SampleDetail: FC = () => {
  const id = useNumericParam('id');
  const [
    {
      date,
      groupMemberId,
      type: dataObjectType,
      periodSkipLastValueDate = false,
    },
  ] = useQueryParams();

  const { state: viewState, isQueryParamCleared } = useLoadViewState(
    'sampleDirectory',
    { filters: {}, sort: [] as SortingState },
  );

  if (!id || !dataObjectType) {
    return <NotFound />;
  }

  if (!isQueryParamCleared) {
    return null;
  }

  return (
    <SampleContextProvider
      id={id}
      date={date as ISODate}
      periodSkipLastValueDate={periodSkipLastValueDate as boolean}
      dataObjectType={dataObjectType as SampleDataObjectType}
      groupMemberId={
        isSafeFiniteInteger(groupMemberId) ? Number(groupMemberId) : undefined
      }
      sampleView={true}
    >
      <SampleDetailDirectory
        initialFilters={viewState.filters}
        initialSort={viewState.sort}
        initialPagination={viewState.pagination}
      />
    </SampleContextProvider>
  );
};

interface SampleDetailDirectoryProps {
  initialFilters: Partial<Record<string, any>>;
  initialSort: SortingState;
  initialPagination: PaginationProps | undefined;
}

const SampleDetailDirectory: FC<SampleDetailDirectoryProps> = (props) => {
  const { initialFilters, initialSort, initialPagination } = props;
  const {
    groupOrDescriptionId: id,
    date,
    sampleType: dataObjectType,
  } = useSampleContext();
  const [{ viewType = 'detail' }, setQueryParams] = useQueryParams<{
    viewType: ViewType;
  }>();
  const isEditing = viewType === 'mutate';

  const { tableProps, paginationProps, queryParameters, setTotal } =
    useDirectory({
      queryFilterKeys: [...FILTER_KEYS],
      initialFilters,
      initialSort,
      initialPagination,
      arrayFields: ['statusIds'],
      numericFields: ['statusIds'],
      serializeFilter: serializeSampleFilter,
      deserializeFilter: deserializeSampleFilter,
      serializeSort: ({ id, desc }) =>
        defaultSerializeSort({
          id: id === 'statusIds' ? 'statusId' : id,
          desc,
        }),
    });

  useSaveDirectoryState('sampleDirectory', {
    paginationProps,
    queryParameters,
    tableProps,
  });

  useEffect(() => {
    saveViewState('sampleView', {
      id,
      date,
      type: dataObjectType,
    });
  }, [id, date, dataObjectType]);

  const scopeKey = SampleKey.create(dataObjectType, id, date);
  const sessionQuery = useSampleEditSessionQuery(
    { scopeKey },
    { enabled: isEditing },
  );
  const { t } = useTranslation();
  const { setError } = useAlertContext();
  useEffect(() => {
    if (isEditing && sessionQuery.isError) {
      setError(t('editSession.openError'));
      setQueryParams({ viewType: 'detail' });
      sessionQuery.remove();
    }
  }, [isEditing, sessionQuery, setError, setQueryParams, t]);
  const updateRowsMutation = useUpdateSampleDraftsMutation();
  const insertRowMutation = useInsertSampleDraftMutation();
  const deleteRowsMutation = useDeleteSampleDraftsMutation();
  const commitMutation = useCommitSampleDraftsMutation();
  const discardMutation = useDiscardSampleEditSessionMutation();
  const validateRowMutation = useValidateSampleRowMutation();

  const samplesQuery = useSamplesQuery(
    {
      id,
      valueDate: date,
      type: dataObjectType,
      sessionId: isEditing ? sessionQuery.data?.id?.toString() : undefined,
      ...queryParameters,
    },
    { placeholderData: keepPreviousData },
  );
  const { data, isLoading, isFetching } = samplesQuery;

  // Editing is blocked until the edit session has been reopened after a commit
  const [committedAt, setCommittedAt] = useState<number>();
  const isReopeningSession =
    committedAt != null &&
    sessionQuery.dataUpdatedAt <= committedAt &&
    !sessionQuery.isError;
  const handleSessionClose = useCallback(
    (isCommit: boolean) => {
      if (isCommit) {
        setCommittedAt(Date.now());
        void sessionQuery.invalidate();
        return;
      }

      flushSync(() => {
        setQueryParams({ viewType: 'detail' });
      });
      sessionQuery.remove();
    },
    [sessionQuery, setQueryParams],
  );

  const handleReload = useCallback(async () => {
    await samplesQuery.refetch();
  }, [samplesQuery]);

  useEffect(() => {
    if (data) setTotal(data.totalElements);
  }, [data, setTotal]);

  useClampPageToBounds(data, paginationProps.onChange);

  return (
    <EditSessionContextProvider
      sessionId={sessionQuery.data?.id}
      updateRowsFn={updateRowsMutation.mutateAsync}
      insertRowsFn={insertRowMutation.mutateAsync}
      deleteRowsFn={deleteRowsMutation.mutateAsync}
      commitFn={commitMutation.mutateAsync}
      discardFn={discardMutation.mutateAsync}
      validateRowFn={validateRowMutation.mutateAsync}
      onSessionClose={handleSessionClose}
      // Samples reload rows after insert (unlike input data): status, data manager,
      // modified at/by and lock state are filled in by the server, and uploaded
      // subjects are not in the form at all.
      refreshRows={samplesQuery.invalidate}
    >
      <SampleDetailsContextProvider>
        {isLoading ? (
          <Spinner />
        ) : !data ? null : (
          <SampleDetailForm
            pageData={data}
            paginationProps={paginationProps}
            tableProps={tableProps}
            isLoading={isFetching || isReopeningSession}
            onReload={handleReload}
          />
        )}
      </SampleDetailsContextProvider>
    </EditSessionContextProvider>
  );
};

const FILTER_KEYS = [
  'lockState',
  'dataSubjectName',
  'statusIds',
  'dataManagerUsername',
  'dataModifiedByUsername',
  'dataModifiedAtFrom',
  'dataModifiedAtTo',
] as const;

const serializeSampleFilter = (id: string, value: unknown) => {
  if (id === 'dataModifiedAt') {
    const [from, to] = Array.isArray(value) ? value : [value, value];
    return { dataModifiedAtFrom: from, dataModifiedAtTo: to };
  }
  return { [id]: value };
};

const deserializeSampleFilter = (
  id: string,
  value: unknown,
  all: Record<string, unknown>,
) => {
  if (id === 'dataModifiedAtFrom' || id === 'dataModifiedAtTo') {
    return { dataModifiedAt: [all.dataModifiedAtFrom, all.dataModifiedAtTo] };
  }
  return { [id]: value };
};
